import ActionType from '@/Common/Enum/ActionType.js';
import nightcrawler, {
  NIGHTCRAWLER_CHECK_STATUS,
  onlyFailing
} from '@/Common/Classifiers/NightcrawlerCheckClassifier.js';
import i18n from '@/Plugins/i18n.js';

const t = (key) => i18n.global.t(`common.dkim_directive.${key}`);

export default nightcrawler({
  _root: [
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DKIM001':
              return t('dkim001');
            case 'DKIM002':
              return t('dkim002');
            case 'DKIM003':
              return t('dkim003');
            case 'DKIM004':
              return t('dkim004');
            case 'DKIM005':
              return c.msg;
            case 'DKIM006':
              return t('dkim006');
            default:
              return c.msg;
          }
        })
    }
  ],
  v: [
    {
      default: true,
      required: true,
      actionType: ActionType.NO_ACTION_WITH_DEFAULT,
      message: t('v_not_specified')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('v_first_directive')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  p: [
    {
      default: true,
      required: true,
      actionType: ActionType.ACTION_REQUIRED,
      value: '',
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      message: t('p_not_specified')
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DKIMP01':
              return t('p_invalid');
            case 'DKIMP02':
              return t('p_not_secure');
            case 'DKIMP03':
              return t('p_may_not_fit');
            default:
              return c.msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  k: [
    {
      default: true,
      required: false,
      actionType: ActionType.NO_ACTION_WITH_DEFAULT,
      message: t('k_default')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('k_not_standard')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  s: [
    {
      default: true,
      required: false,
      actionType: ActionType.NO_ACTION_WITH_DEFAULT,
      message: t('s_default')
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DKIMS01':
              return t('s_not_colon_delimited');
            case 'DKIMS02':
            default:
              return c.msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  h: [
    {
      default: true,
      required: false,
      actionType: ActionType.NO_ACTION_WITH_DEFAULT,
      message: t('h_default')
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DKIMH01':
              return t('h_not_colon_delimited');
            case 'DKIMH02':
            default:
              return c.msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  t: (() => {
    const msgFunc = ({ value }) => {
      return value
        .split(':')
        .map((v) => {
          switch (v) {
            case 'y':
              return t('t_y');
            case 's':
              return t('t_s');
          }
        })
        .join('\n');
    };
    return [
      {
        default: true,
        required: false,
        actionType: ActionType.ACTION_OPTIONAL,
        message: t('t_default')
      },
      {
        status: [
          NIGHTCRAWLER_CHECK_STATUS.WARN,
          NIGHTCRAWLER_CHECK_STATUS.ERROR
        ],
        actionType: ({ status }) =>
          ({
            [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
            [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
          })[status],
        errorMessages: ({ checks }) =>
          onlyFailing(checks).map((c) => {
            switch (c.msg_code) {
              case 'DKIMT01':
                return t('t_not_colon_delimited');
              case 'DKIMT02':
              default:
                return c.msg;
            }
          })
      },
      {
        status: NIGHTCRAWLER_CHECK_STATUS.OK,
        value: ['y', 'y:s', 's:y'],
        actionType: ActionType.NO_ACTION,
        message: msgFunc
      },
      {
        status: NIGHTCRAWLER_CHECK_STATUS.OK,
        actionType: ActionType.NO_ACTION,
        message: msgFunc
      }
    ];
  })(),
  n: [
    {
      default: true,
      required: false,
      actionType: ActionType.ACTION_OPTIONAL,
      message: t('n_default')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  g: [
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.NO_ACTION,
      errorMessages: [t('g_empty')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ]
});
